import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import styles from './index.module.scss';
import { CnButton, CnTooltip, CnBadge } from '@sto/cn-ui';
import { useChatStore } from '@/store';
import { getChatSessionPaging, getUnReadCount } from '@/services/fetchs';
import moment from 'moment';
import { zcRobots } from '@/AIChart/components/RichInput/fastQuestion';

/**
 * 历史对话记录
 * @returns 
 */
const History = ({ robotInfo }: { robotInfo: any }) => {
  const { 
    setHistorySessionId, 
    activeSessionId, 
    setActiveSessionId, 
    setActiveHistoryId, 
    setIsNewChat,
    tempSessionRecord,
    updateTempSession,
    setActiveContentType,
    activeContentType,
    botVisible,
    currentRobotCode,
  } = useChatStore();
  const [historyList, setHistoryList] = useState<any[]>([]);
  const { fixedStyle, unReadCount, setUnReadCount } = useChatStore();
  const contentRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<number>(1);
  const sizeRef = useRef<number>(100);
  const loadingRef = useRef<boolean>(false);
  const hasMoreRef = useRef<boolean>(true);
  const unReadCountRef = useRef<NodeJS.Timeout | null>(null);

  // 是否显示【我的订阅】
  const showMySubscription = useMemo(() => {
    // 政策机器人显示我的订阅
    // TODO：先写死，后续优化成动态配置
    return zcRobots.includes(robotInfo.robotCode);
  }, [robotInfo]);

  // 获取未读的订阅数，TODO：暂时先轮询，后续优化成websocket推送
  useEffect(() => {
    if (botVisible && showMySubscription) {
      fetchUnReadCount();
      unReadCountRef.current = setInterval(() => {
        fetchUnReadCount();
      }, 60000);
    } else {
      clearInterval(unReadCountRef.current);
      unReadCountRef.current = null;
    }
    return () => {
      clearInterval(unReadCountRef.current);
      unReadCountRef.current = null;
    };
  }, [botVisible, showMySubscription]);

  /** 获取未读的订阅数 */
  const fetchUnReadCount = useCallback(() => {
    getUnReadCount().then((res: any) => {
      setUnReadCount(res?.data ?? 0);
    });
  }, []);

  const mergeAppend = useCallback((oldList: any[], newList: any[]) => {
    const map = new Map<any, any>();
    [...oldList, ...newList].forEach((it: any) => {
      const key = it.id ?? `${it.sessionId ?? ''}-${it.lastChatTime}`;
      map.set(key, it);
    });
    return Array.from(map.values());
  }, []);

  /** 获取历史会话列表 */
  const loadPage = useCallback(async (page: number) => {
    if (loadingRef.current || !hasMoreRef.current) return;
    loadingRef.current = true;
    try {
      const res: any = await getChatSessionPaging({ currentPage:page, pageSize: sizeRef.current, platform: 'WEB' });
      const list = (res?.data || []) as any[];
      if (res?.success && list.length) {
        setHistoryList(prev => mergeAppend(prev, list));
        pageRef.current = page;
        const total = Number(res?.total ?? res?.pageInfo?.total ?? 0);
        // 判断是否还有更多：
        // - 如果有 total，则用 页码*size 与 total 比较
        // - 否则用本页返回数量与 size 比较
        if (total > 0) {
          hasMoreRef.current = page * sizeRef.current < total;
        } else {
          hasMoreRef.current = list.length === sizeRef.current;
        }
      } else {
        hasMoreRef.current = false;
      }
    } catch (e) {
      // 网络异常也避免疯狂重试
      hasMoreRef.current = false;
    } finally {
      loadingRef.current = false;
    }
  }, [mergeAppend, currentRobotCode]);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const onScroll = useCallback((e: any) => {
    const target = e?.currentTarget as HTMLDivElement;
    if (!target) return;
    const threshold = 20; // 距离底部 20px 以内触发
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - threshold) {
      if (!loadingRef.current && hasMoreRef.current) {
        loadPage(pageRef.current + 1);
      }
    }
  }, [loadPage]);

  /** 历史会话点击 */
  const onHistoryItemClick = useCallback((item: any) => {
    setActiveSessionId(item.sessionId);
    setHistorySessionId(item.sessionId);
    setActiveHistoryId(item.id);
    setIsNewChat(false);
    setActiveContentType('chat');
  }, []);

  /** 开启新会话 */
  const onNewChatClick = useCallback(() => {
    setActiveSessionId('');
    setHistorySessionId('');
    setActiveHistoryId('');
    setIsNewChat(true);
    setActiveContentType('chat');
  }, []);

  /** 历史会话按最新会话时间（lastChatTime）分组 */
  const groupedList = useMemo(() => {
    // 合并历史记录和临时会话记录
    let allSessions = [...historyList];
    
    // 如果有临时会话记录，添加到最前面
    if (tempSessionRecord) {
      if (tempSessionRecord.isTemp) {
        allSessions = [tempSessionRecord, ...allSessions];
      } else {
        updateTempSession(null);
        // 将完成的临时会话记录添加到历史记录中
        const newList = [tempSessionRecord];
        const newHistoryList = mergeAppend(newList, historyList);
        setHistoryList(newHistoryList);
      }
    }
    
    // 首先过滤掉相同sessionId的项，只保留最新的一项
    const filteredHistoryList = allSessions.reduce((acc: any[], item: any) => {
      const existingIndex = acc.findIndex(existing => existing.sessionId === item.sessionId);
      if (existingIndex === -1) {
        // 如果不存在相同sessionId的项，直接添加
        acc.push(item);
      } else {
        // 如果存在相同sessionId的项，比较时间，保留最新的
        const existing = acc[existingIndex];
        if (moment(item.lastChatTime).isAfter(moment(existing.lastChatTime))) {
          acc[existingIndex] = item;
        }
      }
      return acc;
    }, []);

    const now = moment().startOf('day');
    const buckets: Record<string, any[]> = {
      today: [],
      yesterday: [],
      within3: [],
      within7: [],
      within30: [],
    };
    const olderByDate: Record<string, any[]> = {};

    filteredHistoryList.forEach((item: any) => {
      const d = moment(item.lastChatTime).startOf('day');
      const diff = now.diff(d, 'days');
      if (diff === 0) buckets.today.push(item);
      else if (diff === 1) buckets.yesterday.push(item);
      else if (diff <= 3) buckets.within3.push(item);
      else if (diff <= 7) buckets.within7.push(item);
      else if (diff <= 30) buckets.within30.push(item);
      else {
        const label = d.format('YYYY-MM-DD');
        if (!olderByDate[label]) olderByDate[label] = [];
        olderByDate[label].push(item);
      }
    });

    const result: Array<{ key: string; title: string; items: any[] }> = [];
    if (buckets.today.length) result.push({ key: 'today', title: '今日', items: buckets.today });
    if (buckets.yesterday.length) result.push({ key: 'yesterday', title: '昨日', items: buckets.yesterday });
    if (buckets.within3.length) result.push({ key: 'within3', title: '3天内', items: buckets.within3 });
    if (buckets.within7.length) result.push({ key: 'within7', title: '7天内', items: buckets.within7 });
    if (buckets.within30.length) result.push({ key: 'within30', title: '30天内', items: buckets.within30 });

    Object.keys(olderByDate)
      .sort((a, b) => moment(b).valueOf() - moment(a).valueOf())
      .forEach((label) => result.push({ key: `date-${label}`, title: label, items: olderByDate[label] }));

    return result;
  }, [historyList, tempSessionRecord]);

  const openMySubscription = useCallback(() => {
    setActiveContentType('subscription');
    setActiveSessionId('');
  }, []);

  const openMySubscriptionSetting = useCallback(() => {
    setActiveContentType('setting');
    setActiveSessionId('');
  }, []);

  return <div className={styles.history} style={{ height: fixedStyle.height - 64, width: fixedStyle.width * 0.158, minWidth: 148 }}>
    <div className={styles.historyHeader}>
      <CnButton type="secondary" className={styles.historyHeaderBtn} onClick={onNewChatClick}>
        <img src={"https://imgcdn.sto.cn/B9E90698-3525-4F-16x16-add%201%401x.png"} alt="add" />
        开启新对话
      </CnButton>
    </div>
    <div className={styles.historyContent} ref={contentRef} onScroll={onScroll}>
      {groupedList.map((group) => (
        <div className={styles.historyItem} key={group.key}>
          <div className={styles.historyItemTitle}>{group.title}</div>
          <div className={styles.historyItemContent}>
            {group.items.map((it: any, idx: number) => (
              <div 
                key={it.id ?? `${group.key}-${idx}`}
                className={`${styles.historyItemContentItem} ${activeSessionId === it.sessionId ? styles.active : ''}`}
                onClick={() => onHistoryItemClick(it)}
                data-id={it.id}
                data-session-id={it.sessionId}
              >
                {it.sessionTitle}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
    {showMySubscription && <div className={styles.historyFooter}>
      <div className={styles.historyFooterLine}/>
      <div className={styles.historyFooterContent}>
        <div 
          className={`${styles.leftItem} ${activeContentType === 'subscription' ? styles.active : ''}`}
          onClick={openMySubscription}
        >
          <svg fill="none" version="1.1" width="11.666666030883789" height="11.666666030883789" viewBox="0 0 11.666666030883789 11.666666030883789"><g><path d="M4.083333,0L4.083333,1.1666666L7.583333,1.1666666L7.583333,0L8.75,0L8.75,1.1666666L11.083333,1.1666666C11.405508,1.1666666,11.666666,1.4278367,11.666666,1.75L11.666666,11.083333C11.666666,11.405508,11.405508,11.666666,11.083333,11.666666L0.58333331,11.666666C0.26117003,11.666666,0,11.405508,0,11.083333L0,1.75C0,1.4278367,0.26117003,1.1666666,0.58333331,1.1666666L2.9166665,1.1666666L2.9166665,0L4.083333,0ZM10.5,5.833333L1.1666666,5.833333L1.1666666,10.5L10.5,10.5L10.5,5.833333ZM3.5,7L3.5,8.166666L2.3333333,8.166666L2.3333333,7L3.5,7ZM6.4166665,7L6.4166665,8.166666L5.25,8.166666L5.25,7L6.4166665,7ZM9.333333,7L9.333333,8.166666L8.166666,8.166666L8.166666,7L9.333333,7ZM2.9166665,2.3333333L1.1666666,2.3333333L1.1666666,4.6666665L10.5,4.6666665L10.5,2.3333333L8.75,2.3333333L8.75,3.5L7.583333,3.5L7.583333,2.3333333L4.083333,2.3333333L4.083333,3.5L2.9166665,3.5L2.9166665,2.3333333Z" fill={activeContentType === 'subscription' ? "#FF6700" : "#86909C"} fillOpacity="1"/></g></svg>
          <span style={{ color: activeContentType === 'subscription' ? '#FF6700' : '#86909C' }}>
            我的订阅
          </span>
          {unReadCount ? <CnBadge count={unReadCount} /> : null}
        </div>
        <div 
          className={`${styles.rightItem} ${activeContentType === 'setting' ? styles.active : ''}`}
          onClick={openMySubscriptionSetting}
        >
          <img 
            src={activeContentType === 'setting' ? "https://imgcdn.sto.cn/EB9BD73D-1F97-4C-12x13-%E8%B7%AF%E5%BE%84%401x%20(2).png" : "https://imgcdn.sto.cn/41BE309C-6F0A-43-12x13-%E8%B7%AF%E5%BE%84%401x%20(3).png"} 
            alt="设置图标" 
            title="订阅设置"
          />
        </div>
      </div>
    </div>}
  </div>;
};

export default History;